// ─────────────────────────────────────────────────────────────
// Auth Service — Login, logout, password management
// ─────────────────────────────────────────────────────────────
import {
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  onAuthStateChanged,
} from 'firebase/auth';
import { ref, get }           from 'firebase/database';
import { auth, db }           from '@/firebase';
import { setUser, setAdmin }  from '@/store/tripState';
import { toastSuccess }       from '@/ui/toast';
import { logAction, logError } from '@/services/logger.service';
import { passwordStrength }   from '@/helpers';
import type { TravelleUser }  from '@/types';

export function initAuth(
  onLogin:  (user: TravelleUser, isAdmin: boolean) => void,
  onLogout: () => void
): void {
  onAuthStateChanged(auth, async fbUser => {
    if (fbUser) {
      try {
        const snap  = await get(ref(db, `users/${fbUser.uid}`));
        const data  = (snap.val() ?? {}) as { role?: string; displayName?: string };
        const isAdm = data.role === 'admin';
        const user: TravelleUser = {
          uid:         fbUser.uid,
          displayName: data.displayName ?? fbUser.displayName ?? '',
          email:       fbUser.email ?? '',
          role:        data.role as TravelleUser['role'],
        };
        setUser(user);
        setAdmin(isAdm);
        logAction('login', `Login: ${fbUser.email}`);
        onLogin(user, isAdm);
      } catch {
        setUser(null);
        setAdmin(false);
        onLogout();
      }
    } else {
      setUser(null);
      setAdmin(false);
      onLogout();
    }
  });
}

export async function doLogin(email: string, password: string): Promise<void> {
  try {
    await signInWithEmailAndPassword(auth, email, password);
  } catch (e) {
    logError('login_failed', e, { email });
    throw e;
  }
}

export async function doLogout(): Promise<void> {
  logAction('logout', 'User logged out');
  await signOut(auth);
}

export async function changeOwnPassword(
  currentPassword: string,
  newPassword:     string
): Promise<void> {
  const user = auth.currentUser;
  if (!user?.email) throw new Error('Usuário não autenticado.');
  const s = passwordStrength(newPassword);
  if (!s.valid) throw new Error('A nova senha não atende aos requisitos de segurança.');
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
  logAction('password_changed', 'Own password changed');
  toastSuccess('Senha alterada com sucesso!');
}

export async function changeOtherUserPassword(
  targetUid:   string,
  newPassword: string
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Não autenticado.');
  const s = passwordStrength(newPassword);
  if (!s.valid) throw new Error('A nova senha não atende aos requisitos de segurança.');
  const idToken = await user.getIdToken();
  const fnUrl   = import.meta.env.VITE_CHANGE_PASSWORD_FN_URL as string;
  const resp    = await fetch(fnUrl, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body:    JSON.stringify({ uid: targetUid, newPassword }),
  });
  const data = await resp.json() as { success?: boolean; message?: string; error?: string };
  if (!resp.ok) throw new Error(data.error ?? 'Erro desconhecido.');
  logAction('password_changed', `Admin changed password for uid: ${targetUid}`, { targetUid });
  toastSuccess(data.message ?? 'Senha alterada com sucesso!');
}

export function authErrorMessage(code: string): string {
  const msgs: Record<string, string> = {
    'auth/invalid-credential':    'E-mail ou senha incorretos.',
    'auth/user-not-found':        'Usuário não encontrado.',
    'auth/wrong-password':        'Senha incorreta.',
    'auth/too-many-requests':     'Muitas tentativas. Tente mais tarde.',
    'auth/requires-recent-login': 'Sessão expirada. Faça logout e login novamente.',
  };
  return msgs[code] ?? 'Erro de autenticação.';
}
